<template>
  <div class="animated fadeIn">
    <b-card>
      <div slot="header">
        <b-row>
          <b-col cols="6">
            <span v-if="isUpdateForm">{{ $dt('vacations.type_update_title', 'Update vacation type') }}</span>
            <span v-else>{{ $dt('vacations.type_create_title', 'Create vacation type') }}</span>
          </b-col>
        </b-row>
      </div>
      <div v-if="spinners.contentIsLoading" class="text-center">
        <b-spinner type="grow" variant="primary" />
      </div>
      <div v-else>
        <b-form-group :invalid-feedback="errorMsg?.name || ''">
          <label>{{ $dt('vacations.type_name', 'Name') }}</label>
          <b-form-input type="text" v-model="entity.name" :class="{ 'is-invalid': !!errorMsg?.name }"
            :placeholder="$dt('vacations.type_name', 'Name')" />
        </b-form-group>
        <b-form-group :invalid-feedback="errorMsg?.description || ''">
          <label>{{ $dt('vacations.jira-tasks', 'Assigned Jira Tasks') }}</label>
          <treeselect
            v-model="entity.jira_tasks_ids"
            :clearable="true"
            :multiple="true"
            :async="true"
            :cacheOptions="false"
            :defaultOptions="issuesOptions"
            :load-options="optionsRequest"
            placeholder="Select Jira tasks"
            :max-height="200"
          />
        </b-form-group>
        <b-form-group :invalid-feedback="errorMsg?.description || ''">
          <label>{{ $dt('vacations.type_description', 'Description') }}</label>
          <b-form-textarea type="text" v-model="entity.description" :class="{ 'is-invalid': !!errorMsg?.description }"
            :placeholder="$dt('vacations.type_name', 'Description')" />
        </b-form-group>
        <b-form-checkbox v-model="entity.is_active">
          {{ $dt('vacations.type_is_active', 'Is active:') }}
        </b-form-checkbox>
        <b-form-checkbox v-model="entity.noteable">
          {{ $dt('vacation_type.form_noteable_label', 'Noteable:') }}
        </b-form-checkbox>
      </div>
      <div slot="footer">
        <b-button type="submit" variant="primary" :disabled="spinners.isSaving" @click="save()">
          <b-spinner v-if="spinners.isSaving" type="grow" small />
          {{ $t('global.save') }}
        </b-button>
        <b-button :to="prevRoute ? prevRoute : { name: 'VacationTypesList' }" variant="info" class="ml-2">
          {{ $dt('global.cancel-back', 'Back') }}
        </b-button>
      </div>
    </b-card>
  </div>
</template>

<script>
import Services from '@/services/index'
import Treeselect from '@riophae/vue-treeselect'

export default {
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.name !== null) {
        const { name, params, query } = from
        vm.prevRoute = { name, params, query }
      }
    })
  },
  mounted() {
    this.load()
  },
  data() {
    return {
      prevRoute: null,
      spinners: {
        contentIsLoading: false,
        isSaving: false,
        tasksLoading: false,
      },
      issuesOptions: [],
      entity: this.value,
      errorMsg: this.errors,
      debounce: null,
    }
  },
  props: {
    // If id === null, form will be considered create form else update
    id: { type: Number, default: null },
    // pass errors from parent form if embedded
    errors: { type: Object, default() { return {} } },
    // value is used both as DEFAULTs placeholder and for v-model
    value: {
      type: Object,
      default() {
        return {
          name: '',
          description: null,
          jira_tasks_ids: [],
          is_active: false,
          noteable: false,
        }
      },
    },
  },
  components: {
    Treeselect
  },
  computed: {
    isUpdateForm() { return this.id !== null },
  },
  methods: {
    getForUpdate: Services.vacationTypes.editData,
    getForCreate: Services.vacationTypes.createData,
    pushUpdate: Services.vacationTypes.update,
    pushCreate: Services.vacationTypes.create,
    load() {
      this.spinners.contentIsLoading = true
      const action = this.isUpdateForm ? this.getForUpdate : this.getForCreate
      action(this.id).then((data) => {
        this.spinners.contentIsLoading = false
        this.entity = this.isUpdateForm ? data.data.entity : { ...this.entity, ...data.data.entity }
        if (data?.data?.entity?.jira_issues?.length > 0) {
          // parse predefined options
          this.issuesOptions = data.data.entity.jira_issues.map((issue) => {
            let name = issue.summary
            if (issue?.summary?.length > 20) name = name.substring(0, 20) + '...'
            return { id: parseInt(issue.id), label: issue.jira_key + ': ' + name }
          })
          // set entity tasks ids
          this.entity.jira_tasks_ids = data.data.entity.jira_issues.map((issue) => (parseInt(issue.id)))
        } else {
          this.entity.jira_tasks_ids = []
        }
      }).catch((data) => {
        this.alertError({ title: 'Failed to get vacation type', text: '-' })
        this.spinners.contentIsLoading = false
        this.errorMsg = data.data ? data.data.errors : {}
      })
    },
    save() {
      this.spinners.isSaving = true
      const action = this.isUpdateForm ? this.pushUpdate : this.pushCreate
      action(this.entity).then((data) => {
        this.spinners.isSaving = false
        this.alertSuccess({ title: 'Vacation type saved', text: '-' })
        this.errorMsg = {}
        if (!this.isUpdateForm) {
          this.$router.push({ name: 'VacationTypesList' })
        }
      }).catch((error) => {
        this.alertError({ title: 'Failed to save vacation type', text: '-' })
        this.spinners.isSaving = false
        this.errorMsg = error.errors || {}
        console.log(error)
      })
    },
    optionsRequest({ action, searchQuery, callback }) {
      if(searchQuery?.length < 3) {
        callback(null, [])
        return;
      }
      if (this.debounce) {
        clearTimeout(this.debounce);
      }
      this.debounce = setTimeout(() => {
        this.spinners.tasksLoading = true;
        Services.jira.getListForAutocomplete(searchQuery).then((response) => {
          callback(null, response.data)
        }).catch((e) => {
          console.error('Got error when listing jira tasks.', e);
        }).finally(() => {
          this.spinners.tasksLoading = false;
        });
      }, 500);
    },
  }
}
</script>

<style scoped></style>
